import React, { useEffect, useMemo, useRef, useState } from "react";
import Countdown, { CountdownRenderProps, zeroPad } from "react-countdown";
import { ArrowLeft, Check, Clock } from "lucide-react";

import { useSubmitExamMutation } from "src/lib/endpoints/question/question.api";
import {
  Question,
  QuestionData,
} from "src/lib/endpoints/question/question.type";
import { selectTestState } from "src/lib/features/test/testSlice";
import { useTypedSelector } from "src/lib/store/store";
import { cn } from "src/lib/utils/utils";
import QuestionNav from "./QuestionNav";
import ScoreDialog from "./ScoreDialog";
import { Card, Eyebrow, Meter, OutlineButton, PrimaryButton, Shell } from "./ui";

const LETTERS = ["A", "B", "C", "D"];

type Option = [key: string, text: string | null];

const keyOf = (question: Question) => String(question.id);

/* Fisher-Yates. Sorting on a random comparator, as the old screen does, is
   biased towards the original order. */
const shuffled = <T,>(items: T[]): T[] => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/**
 * The exam itself.
 *
 * Unlike `pages/Test/TakeTest.tsx`, a talent can move freely between
 * questions: back and forward, or straight to any number in the question list,
 * and change an answer until they submit. The answer payload is the same one
 * the old screen sends. As before, it carries every question the talent has
 * opened, with an empty `option` for those left blank, and the test still
 * submits itself when the clock runs out.
 *
 * Each question's options are shuffled once, when the test starts, so going
 * back to a question shows its answers in the order they were first read.
 */
const Exam = ({ onFinished }: { onFinished?: () => void }) => {
  const { submitExam } = useSubmitExamMutation();
  const { questionData } = useTypedSelector(selectTestState);
  const { questions, count, testDuration } = questionData as QuestionData;
  const total = questions?.length ?? 0;

  const [current, setCurrent] = useState(0);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [opened, setOpened] = useState<Record<string, true>>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [showScore, setShowScore] = useState(false);
  /* Drives the timer chip's colour. The countdown owns the number; this only
     needs to know which of three bands it is in. */
  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);

  const options = useMemo(() => {
    const byQuestion: Record<string, Option[]> = {};
    (questions ?? []).forEach((question) => {
      byQuestion[keyOf(question)] = shuffled<Option>([
        ["correctOption1", question.correctOption1],
        ["option2", question.option2],
        ["option3", question.option3],
        ["option4", question.option4],
      ]);
    });
    return byQuestion;
  }, [questions]);

  const question = questions?.[current];
  const currentKey = question ? keyOf(question) : "";
  const chosen = selections[currentKey] ?? "";
  const isLast = current === total - 1;
  const answeredCount = (questions ?? []).filter(
    (q) => !!selections[keyOf(q)]
  ).length;
  const unanswered = total - answeredCount;

  useEffect(() => {
    if (!currentKey) return;
    setOpened((prev) => (prev[currentKey] ? prev : { ...prev, [currentKey]: true }));
  }, [currentKey]);

  /* The countdown is built once, so its onComplete would otherwise see the
     answers as they were on the first render (none). Refs give it the latest. */
  const completedRef = useRef(false);
  const submitRef = useRef<() => void>(() => {});

  const submit = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setIsCompleted(true);
    setSubmitFailed(false);
    setLoading(true);

    // The question on screen counts as opened even if its effect hasn't run.
    const seen = { ...opened, [currentKey]: true };
    const answers = questions
      .filter((q) => seen[keyOf(q)])
      .map((q) => ({
        questionID: q.id,
        framework_id: q.framework_id,
        language_id: q.language_id,
        skills_id: q.skills_id,
        question_dependency: q.question_dependency,
        option: selections[keyOf(q)] ?? "",
      }));

    submitExam(
      { answers, count },
      {
        onSuccess: () => {
          setLoading(false);
          setShowScore(true);
          onFinished?.();
        },
        onError: () => {
          // Let them try again rather than leave a submitted-looking test.
          completedRef.current = false;
          setIsCompleted(false);
          setLoading(false);
          setSubmitFailed(true);
        },
      }
    );
  };

  submitRef.current = submit;

  const renderer = ({ minutes, seconds, completed }: CountdownRenderProps) => (
    <span className="tabular-nums">
      {completed ? "00:00" : `${zeroPad(minutes)}:${zeroPad(seconds)}`}
    </span>
  );

  const countDown = useMemo(
    () => (
      <Countdown
        date={Date.now() + +(testDuration || 0) * 60 * 1000}
        renderer={renderer}
        autoStart
        onTick={({ minutes }) => setMinutesLeft(minutes)}
        onComplete={() => {
          if (testDuration === undefined) return;
          submitRef.current();
        }}
      />
    ),
    [testDuration]
  );

  useEffect(() => {
    const stopPropagation = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Data will be lost if you leave the page, are you sure?";
    };

    window.addEventListener("beforeunload", stopPropagation);
    return () => window.removeEventListener("beforeunload", stopPropagation);
  }, []);

  const goTo = (index: number) => {
    if (index < 0 || index >= total || index === current) return;
    setCurrent(index);
    // On a phone the question list sits above the question; bring the
    // question's top back into view after a jump from further down.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const choose = (option: string) => {
    if (isCompleted) return;
    setSelections((prev) => ({ ...prev, [currentKey]: option }));
  };

  if (!questions || total === 0 || testDuration === undefined || !question) {
    return null;
  }

  /* Under five minutes the clock turns amber, under one minute red. Colour is
     not the only signal — the chip keeps its icon and its reading — so this
     still works for a visitor who cannot separate the two hues. */
  const timerTone =
    minutesLeft === null || minutesLeft >= 5
      ? "border-border bg-surface text-brand-navy"
      : minutesLeft >= 1
      ? "border-amber-300 bg-amber-50 text-amber-700"
      : "border-red-300 bg-red-50 text-red-700";

  const hint = submitFailed
    ? "We couldn't submit your test. Check your connection and try again."
    : isLast && unanswered > 0
    ? `${unanswered} question${unanswered === 1 ? "" : "s"} unanswered. They will count as wrong if you submit now.`
    : chosen
    ? "Answer selected."
    : "No answer selected. You can come back to it.";

  /* The test cannot be copied: no selecting, copying, cutting or right-click
     menu. A deterrent only: a screenshot or a phone camera cannot be blocked. */
  const blockCopy = (event: React.SyntheticEvent) => {
    event.preventDefault();
  };

  return (
    <div
      className="select-none"
      onCopy={blockCopy}
      onCut={blockCopy}
      onContextMenu={blockCopy}
    >
      <Shell className="max-w-5xl">
        {showScore && <ScoreDialog resultsPath="/results" />}

        <div className="flex flex-col gap-5">
          {/* Position and clock span the full width, above both the question
              list and the question. */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <Eyebrow>
                Question {current + 1} of {total}
              </Eyebrow>

              {!showScore && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold transition-colors",
                    timerTone
                  )}
                >
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">Time remaining:</span>
                  {countDown}
                </span>
              )}
            </div>

            {/* With free movement, position says little about progress. The
                bar reports answers given instead. */}
            <Meter
              value={(answeredCount / total) * 100}
              label={`${answeredCount} of ${total} questions answered`}
            />
          </div>

          <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-start lg:gap-8">
            <QuestionNav
              total={total}
              current={current}
              isAnswered={(index) => !!selections[keyOf(questions[index])]}
              answeredCount={answeredCount}
              onSelect={goTo}
              disabled={isCompleted}
            />

            <div className="flex flex-col gap-5">
              <Card className="flex flex-col gap-6">
                <h1 className="text-lg font-semibold leading-relaxed text-brand-navy sm:text-xl">
                  {question.question ?? ""}
                </h1>

                {/* Real radios, visually replaced. The whole row is the control,
                    so the target is the full width of the card rather than a
                    12px circle, and arrow keys move between options for free. */}
                <fieldset className="flex flex-col gap-3" disabled={isCompleted}>
                  <legend className="sr-only">Choose one answer</legend>

                  {(options[currentKey] ?? []).map(([option, text], index) => {
                    const selected = chosen === option;

                    return (
                      <label
                        key={option}
                        className={cn(
                          "group flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors",
                          selected
                            ? "border-brand-orange bg-brand-orange-100/50"
                            : "border-border bg-card hover:border-brand-navy/25 hover:bg-surface"
                        )}
                      >
                        <input
                          type="radio"
                          name={`question-${currentKey}`}
                          value={option}
                          checked={selected}
                          onChange={() => choose(option)}
                          className="sr-only"
                        />

                        <span
                          aria-hidden="true"
                          className={cn(
                            "grid h-7 w-7 shrink-0 place-items-center rounded-lg text-xs font-bold transition-colors",
                            selected
                              ? "bg-brand-orange text-white"
                              : "bg-brand-navy-100 text-brand-navy"
                          )}
                        >
                          {selected ? <Check className="h-4 w-4" /> : LETTERS[index]}
                        </span>

                        <span className="pt-0.5 text-sm leading-relaxed text-brand-navy">
                          {text}
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </Card>

              <p
                className={cn(
                  "text-xs",
                  submitFailed ? "font-semibold text-red-700" : "text-muted-foreground"
                )}
                aria-live="polite"
              >
                {hint}
              </p>

              <div className="flex items-center justify-between gap-3">
                <OutlineButton
                  onClick={() => goTo(current - 1)}
                  disabled={current === 0 || isCompleted}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Previous
                </OutlineButton>

                <PrimaryButton
                  className="whitespace-nowrap"
                  onClick={isLast ? submit : () => goTo(current + 1)}
                  disabled={loading || isCompleted}
                >
                  {loading ? "Submitting…" : isLast ? "Submit test" : "Next"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </Shell>
    </div>
  );
};

export default Exam;
